import { Building2, Loader2 } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { Button } from "@/components/ui/button"
import { Combobox, type ComboboxOption } from "@/components/ui/combobox"
import { DialogClose, DialogContent } from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

import type { ClientMember, ClientRow, ClientUserOption, UserId } from "./clients-data"
import type { CreateClientInput } from "./clients-store"

const pluralize = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`

export function GrantAccessDialog({
  engagement,
  user,
  onConfirm,
}: {
  engagement: ClientRow
  user: ClientUserOption
  onConfirm: () => void
}): JSX.Element {
  return (
    <DialogContent
      title={`Grant ${user.name} access?`}
      description={`They will be added as a member of ${engagement.name}.`}
      size="sm"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            Grant access
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-body-sm leading-normal text-text-2">
        <p>
          They will see{" "}
          <span className="font-semibold text-text">
            all {pluralize(engagement.reviewCount, "review", "reviews")}
          </span>{" "}
          under this engagement, including every report already sent — client access is to the
          engagement, not to individual reviews.
        </p>
        <div className="flex items-center gap-2.5 rounded-md border border-border-soft bg-surface-sunken px-3 py-2">
          <Avatar name={user.name} identity={user.id} size="sm" />
          <div className="min-w-0">
            <div className="truncate text-body-sm font-medium text-text">{user.name}</div>
            <div className="truncate font-id text-caption text-text-3">{user.email}</div>
          </div>
        </div>
      </div>
    </DialogContent>
  )
}

export function RevokeAccessDialog({
  engagement,
  member,
  onConfirm,
}: {
  engagement: ClientRow
  member: ClientMember
  onConfirm: () => void
}): JSX.Element {
  return (
    <DialogContent
      title={`Revoke ${member.name}'s access?`}
      description="Their account stays — only this engagement is revoked."
      size="sm"
      footer={
        <>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="default"
            size="sm"
            className="border-err bg-err/10 text-err hover:bg-err/15"
            onClick={onConfirm}
          >
            Revoke access
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-body-sm leading-normal text-text-2">
        <p>
          They lose access to{" "}
          <span className="font-semibold text-text">
            all {pluralize(engagement.reviewCount, "review", "reviews")}
          </span>{" "}
          under {engagement.name}, immediately and on their next page load.
        </p>
        {member.isPrimary && (
          <p className="rounded-md border border-print/40 bg-print-tint/60 px-3 py-2 text-body-sm text-text">
            <span className="font-semibold">{member.name}</span> is the primary contact for this
            engagement. Reports and notifications currently address them — nominate someone else
            first if that matters.
          </p>
        )}
      </div>
    </DialogContent>
  )
}

export function NewEngagementDialog({
  users,
  onSubmit,
  isSubmitting = false,
}: {
  users: readonly ClientUserOption[]
  onSubmit: (draft: CreateClientInput) => void
  isSubmitting?: boolean
}): JSX.Element {
  const [name, setName] = React.useState("")
  const [contact, setContact] = React.useState<UserId | null>(null)
  const [notes, setNotes] = React.useState("")

  const canSubmit = name.trim().length > 0 && !isSubmitting

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    onSubmit({
      name: name.trim(),
      primaryContactId: contact,
      notes: notes.trim() || null,
    })
  }

  const contactOptions: ComboboxOption[] = users.map((u) => ({
    value: u.id,
    label: `${u.name} — ${u.email}`,
  }))

  const consequence = contact ? undefined : "Nobody can see it until you add a member."

  return (
    <DialogContent
      title="New engagement"
      description="Sets the boundary that scopes a client login to its own reviews."
      size="md"
      footer={
        <>
          {consequence && <span className="mr-auto text-caption text-text-3">{consequence}</span>}
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            leadingIcon={isSubmitting ? <Loader2 className="animate-spin" /> : <Building2 />}
            disabled={!canSubmit}
            onClick={submit}
          >
            {isSubmitting ? "Creating…" : "Create"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <FormField label="Client name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            placeholder="Acme Crop Science"
            autoFocus
          />
        </FormField>

        <FormField
          label="Primary contact"
          optional
          hint="Reports and notifications address this person. Can be assigned later."
        >
          <Combobox
            options={contactOptions}
            value={contact}
            onValueChange={(v) => setContact(v as UserId | null)}
            placeholder="Search client accounts"
            searchPlaceholder="Search by name or email"
          />
        </FormField>

        <FormField
          label="Notes"
          optional
          hint="Engagement scope, portfolio, anything the review team should know."
        >
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            placeholder="Optional context for the review team"
          />
        </FormField>
      </form>
    </DialogContent>
  )
}
